import { z } from "zod";

// Strong password schema
const strongPasswordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    "Password must contain at least one uppercase letter, one lowercase letter, and one number",
  );

// Email schema
const emailSchema = z
  .string()
  .email("Please enter a valid email address")
  .toLowerCase()
  .trim();

// Base schema without confirmPassword
const baseCaseWorkerRegistrationSchema = z.object({
  agency: z
    .string()
    .min(1, "Agency is required")
    .max(100, "Agency name must be less than 100 characters")
    .trim(),
  areaIds: z
    .array(z.string().min(1, "Please select valid areas"))
    .min(1, "Please select at least one area to serve"),
  church: z
    .string()
    .max(100, "Church name must be less than 100 characters")
    .trim()
    .optional(),
  city: z.string().min(1, "City is required"),
  country: z.string().transform((val) => val || "US"),
  email: emailSchema,
  experience: z
    .string()
    .max(1000, "Experience description must be less than 1000 characters")
    .optional(),
  first_name: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .trim(),
  last_name: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .trim(),
  organizationId: z.string().min(1, "Organization is required").optional(),
  password: strongPasswordSchema,
  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine(
      (val) => /^[+]?[1-9][\d]{0,15}$/.test(val.replace(/[\s\-().]/g, "")),
      "Please enter a valid phone number",
    ),
  primaryAreaId: z.string().min(1, "Primary area is required").optional(),
  state: z
    .string()
    .min(2, "State is required")
    .max(2, "State must be 2 characters"),
  street_address: z.string().min(1, "Street address is required"),
  zip_code: z
    .string()
    .min(5, "ZIP code must be at least 5 characters")
    .max(10, "ZIP code must be at most 10 characters"),
});

// Extend with confirmPassword and add refine
export const caseWorkerRegistrationSchema = baseCaseWorkerRegistrationSchema
  .safeExtend({
    confirmPassword: strongPasswordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })
  .refine(
    (data) => {
      // If primaryAreaId is provided, it must be one of the selected areas
      if (data.primaryAreaId) {
        return data.areaIds.includes(data.primaryAreaId);
      }

      return true;
    },
    {
      message: "Primary area must be one of the selected service areas",
      path: ["primaryAreaId"],
    },
  );

export type CaseWorkerRegistrationData = z.infer<
  typeof caseWorkerRegistrationSchema
>;
